import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { XMarkIcon } from '@heroicons/react/24/outline';

const SEEN_KEY = 'tm_popup_seen';

export default function WelcomePopup() {
  const { t } = useTranslation();
  const localhost = process.env.REACT_APP_LOCALHOST;

  const [settings, setSettings] = useState(null);
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [code, setCode] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (localStorage.getItem(SEEN_KEY)) return;
    let timer;
    (async () => {
      try {
        const res = await axios.get(localhost + 'site-settings');
        if (res.data?.popup_enabled) {
          setSettings(res.data);
          timer = setTimeout(() => setOpen(true), 2500);
        }
      } catch (e) {
        // ignore
      }
    })();
    return () => timer && clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const dismiss = () => {
    localStorage.setItem(SEEN_KEY, '1');
    setOpen(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError(t('popup-invalid-email'));
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(localhost + 'popup-subscribe', { nl_email: email });
      localStorage.setItem(SEEN_KEY, '1');
      setCode(res.data?.code || null);
      if (!res.data?.code) {
        // No code configured — just thank them and close shortly.
        setTimeout(() => setOpen(false), 1500);
      }
    } catch (err) {
      setError(err?.response?.data?.error ? t('popup-error') : t('popup-error'));
    } finally {
      setLoading(false);
    }
  };

  if (!open || !settings) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center px-4">
      <div className="absolute inset-0 bg-black/60" onClick={dismiss} />
      <div className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden">
        <button type="button" onClick={dismiss} aria-label="Close" className="absolute right-3 top-3 z-10 text-gray-400 hover:text-black">
          <XMarkIcon className="w-6 h-6" />
        </button>

        <div className="bg-black text-white text-center py-4 text-lg tracking-[0.2em] font-semibold">THUNDERMAROC</div>

        <div className="p-6 text-center">
          {code ? (
            <>
              <h3 className="text-xl font-bold text-gray-900 mb-2">{t('popup-thanks-title')}</h3>
              <p className="text-sm text-gray-600 mb-4">{t('popup-thanks-text')}</p>
              <div className="text-2xl font-bold tracking-widest border-2 border-dashed border-black rounded-lg py-3 px-4 inline-block">
                {code}
              </div>
              <p className="text-xs text-gray-400 mt-4">{t('popup-code-emailed')}</p>
              <button type="button" onClick={dismiss} className="mt-5 w-full rounded-full bg-black text-white py-3 text-sm uppercase tracking-wider font-medium hover:bg-black/85">
                {t('popup-continue')}
              </button>
            </>
          ) : (
            <>
              <h3 className="text-xl font-bold text-gray-900 mb-2">
                {settings.popup_title || t('popup-default-title')}
              </h3>
              <p className="text-sm text-gray-600 mb-5">
                {settings.popup_subtitle || t('popup-default-subtitle')}
              </p>
              <form onSubmit={submit} className="space-y-3">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t('email')}
                  className="w-full rounded-full border border-gray-300 px-4 py-3 text-sm outline-none focus:border-black"
                />
                {error && <p className="text-xs text-red-500">{error}</p>}
                <button type="submit" disabled={loading} className="w-full rounded-full bg-black text-white py-3 text-sm uppercase tracking-wider font-medium hover:bg-black/85 disabled:bg-black/40">
                  {loading ? t('processing') : t('popup-cta')}
                </button>
              </form>
              <button type="button" onClick={dismiss} className="mt-3 text-xs text-gray-400 hover:text-gray-600 underline">
                {t('popup-no-thanks')}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
